// Khung điều hướng trên ĐIỆN THOẠI — gốc chép theo app iOS (ShopTrackiOS/Features/Shell: MainTab, AgencyTab, CTVTab,
// MoreMenu, ShopMenuPopover). Spec: docs/superpowers/specs/2026-10-14-mobile-giong-app-design.md
// 30/10/2026 (yêu cầu user) web đổi khác app: Bán hàng có tab Đối soát thay Vận chuyển; nút "Thêm" thành "Cài đặt"
// ghim cuối thanh (gồm cả menu tài khoản cũ); các mục của "Thêm" cũ nối tiếp 4 tab, vuốt ngang để thấy (phoneDockItems).
//
// Module THUẦN, không import gì (kể cả React / lucide / '@/…') — test ở tests/unit/web-phone-nav.test.ts.
// Icon chỉ là khoá; shell-model.ts đổi khoá thành component lucide.

export type PhoneWorkMode = 'manage' | 'sell'
export type PhoneSurface = 'sell' | 'agency-manage' | 'ctv-manage' | 'admin'

export type PhoneIcon =
  | 'home' | 'dashboard' | 'orders' | 'products' | 'shipping' | 'network' | 'store' | 'shopOrders'
  | 'money' | 'customers' | 'reports' | 'rates' | 'commission' | 'switch' | 'settings' | 'admin'
  | 'pickup' | 'tariff' | 'bank' | 'security' | 'notifications' | 'feedback' | 'costTable' | 'downline'

export interface PhoneLink {
  to: string
  label: string
  icon: PhoneIcon
}

export type PhoneMoreItem =
  | ({ kind: 'link' } & PhoneLink)
  | { kind: 'switch'; mode: PhoneWorkMode; label: string; icon: 'switch' }

/** Trang Vận chuyển (routes/Shipping.tsx): nội dung Hãng vận chuyển của Cài đặt, đứng riêng như tab của app. */
const SHIPPING: PhoneLink = { to: '/shipping', label: 'Vận chuyển', icon: 'shipping' }

const TABS: Record<PhoneSurface, PhoneLink[]> = {
  sell: [
    { to: '/app', label: 'Tổng quan', icon: 'home' },
    { to: '/orders', label: 'Đơn hàng', icon: 'orders' },
    { to: '/products', label: 'Sản phẩm', icon: 'products' },
    { to: '/reconcile', label: 'Đối soát', icon: 'money' },
  ],
  // Quản lý không có tab Đơn hàng (31/10/2026): đơn của cấp dưới xem từ sơ đồ — "Xem đơn" trong ngăn một đơn vị
  'agency-manage': [
    { to: '/agency/overview', label: 'Tổng quan', icon: 'dashboard' },
    { to: '/network', label: 'Hệ thống', icon: 'network' },
    SHIPPING,
    { to: '/reconcile', label: 'Đối soát', icon: 'money' },
  ],
  'ctv-manage': [
    { to: '/ctv/dashboard', label: 'Tổng quan', icon: 'home' },
    { to: '/network', label: 'Hệ thống', icon: 'store' },
    { to: '/reconcile', label: 'Đối soát', icon: 'money' },
  ],
  // Admin dùng web thành viên rất hiếm trên điện thoại: giữ 4 tab cũ.
  admin: [
    { to: '/agency/overview', label: 'Tổng quan', icon: 'dashboard' },
    { to: '/network', label: 'Mạng lưới', icon: 'network' },
    { to: '/agency/orders', label: 'Đơn hàng', icon: 'orders' },
    { to: '/reconcile', label: 'Đối soát', icon: 'money' },
  ],
}

const link = (to: string, label: string, icon: PhoneIcon): PhoneMoreItem => ({ kind: 'link', to, label, icon })

const MORE: Record<PhoneSurface, PhoneMoreItem[]> = {
  sell: [
    link('/customers', 'Khách hàng', 'customers'),
    link('/reports', 'Báo cáo', 'reports'),
    { kind: 'link', ...SHIPPING },
  ],
  'agency-manage': [
    link('/rates', 'Bảng giá', 'rates'),
    link('/commissions', 'Chiết khấu', 'commission'),
    link('/reports', 'Báo cáo', 'reports'),
  ],
  'ctv-manage': [
    link('/rates', 'Bảng giá', 'rates'),
    link('/commissions/me', 'Chiết khấu', 'commission'),
    link('/reports', 'Báo cáo', 'reports'),
  ],
  admin: [link('/settings', 'Cài đặt', 'settings'), link('/admin', 'Quản trị', 'admin')],
}

/** Shop / admin không có chế độ; Đại lý / CTV theo chế độ đang dùng. */
export function phoneSurface(tenantType: string | null | undefined, mode: PhoneWorkMode): PhoneSurface {
  if (tenantType === 'admin' || tenantType === 'super_admin') return 'admin'
  if (mode === 'manage' && tenantType === 'agency') return 'agency-manage'
  if (mode === 'manage' && tenantType === 'ctv') return 'ctv-manage'
  return 'sell'
}

/** Quyền kiểm theo đường dẫn KHÔNG có query (hasAccess không hiểu "?tab="). */
const pathOf = (to: string) => to.split('?')[0] ?? to

export function phoneTabs(surface: PhoneSurface, canAccess: (to: string) => boolean): PhoneLink[] {
  return TABS[surface].filter((t) => canAccess(pathOf(t.to)))
}

/** Mục cuối của Đại lý / CTV luôn là "Sang …" (đổi chế độ), như MoreMenu của app. */
export function phoneMoreItems(
  surface: PhoneSurface,
  tenantType: string | null | undefined,
  canAccess: (to: string) => boolean,
): PhoneMoreItem[] {
  const items = MORE[surface].filter((i) => i.kind !== 'link' || canAccess(pathOf(i.to)))
  const canSwitch = tenantType === 'agency' || tenantType === 'ctv'
  if (canSwitch && surface === 'sell') items.push({ kind: 'switch', mode: 'manage', label: 'Sang Quản lý', icon: 'switch' })
  if (surface === 'agency-manage' || surface === 'ctv-manage') {
    items.push({ kind: 'switch', mode: 'sell', label: 'Sang Bán hàng', icon: 'switch' })
  }
  return items
}

const ACCOUNT_SHOP: PhoneLink[] = [
  { to: '/settings?tab=warehouse', label: 'Kho lấy hàng', icon: 'pickup' },
  { to: '/rates', label: 'Bảng giá', icon: 'tariff' },
  { to: '/settings?tab=carriers', label: 'Hãng vận chuyển', icon: 'shipping' },
  { to: '/settings?tab=banks', label: 'Ngân hàng', icon: 'bank' },
  { to: '/settings?tab=security', label: 'Bảo mật', icon: 'security' },
  { to: '/settings?tab=notifications', label: 'Thông báo', icon: 'notifications' },
  { to: '/feedback', label: 'Góp ý', icon: 'feedback' },
]

const ACCOUNT_AGENCY: PhoneLink[] = [
  { to: '/settings?tab=warehouse', label: 'Kho lấy hàng', icon: 'pickup' },
  { to: '/settings?tab=carriers', label: 'Hãng vận chuyển', icon: 'shipping' },
  { to: '/rates', label: 'Giá nhập của tôi', icon: 'costTable' },
  { to: '/settings?tab=banks', label: 'Ngân hàng', icon: 'bank' },
  { to: '/settings?tab=security', label: 'Bảo mật', icon: 'security' },
  { to: '/settings?tab=notifications', label: 'Thông báo', icon: 'notifications' },
  { to: '/feedback', label: 'Góp ý', icon: 'feedback' },
]

const ACCOUNT_CTV: PhoneLink[] = [
  { to: '/settings?tab=warehouse', label: 'Kho lấy hàng', icon: 'pickup' },
  { to: '/rates', label: 'Giá nhập của tôi', icon: 'costTable' },
  { to: '/settings?tab=banks', label: 'Ngân hàng', icon: 'bank' },
  { to: '/settings?tab=security', label: 'Bảo mật', icon: 'security' },
  { to: '/settings?tab=notifications', label: 'Thông báo', icon: 'notifications' },
  { to: '/feedback', label: 'Góp ý', icon: 'feedback' },
]

const ACCOUNT_ADMIN: PhoneLink[] = [
  { to: '/settings?tab=security', label: 'Bảo mật', icon: 'security' },
  { to: '/settings?tab=notifications', label: 'Thông báo', icon: 'notifications' },
  { to: '/feedback', label: 'Góp ý', icon: 'feedback' },
]

const DOWNLINE: PhoneLink = { to: '/feedback?tab=downline', label: 'Góp ý cấp dưới', icon: 'downline' }

/** `canDownline` = canHandleDownline(tenantType, role) — chỉ Đại lý / CTV mới có mục "Ticket cấp dưới". */
export function accountMenuItems(tenantType: string | null | undefined, canDownline: boolean): PhoneLink[] {
  if (tenantType === 'admin' || tenantType === 'super_admin') return ACCOUNT_ADMIN
  if (tenantType === 'agency') return canDownline ? [...ACCOUNT_AGENCY, DOWNLINE] : ACCOUNT_AGENCY
  if (tenantType === 'ctv') return canDownline ? [...ACCOUNT_CTV, DOWNLINE] : ACCOUNT_CTV
  return ACCOUNT_SHOP
}

/**
 * Link có khớp trang đang mở không.
 *  - `/x`            → `/x` hoặc trang con `/x/...`
 *  - `/x?tab=v`      → `/x` với đúng `?tab=v`, hoặc route `/x/v` (Cài đặt có cả `/settings/:tab`)
 */
export function linkMatches(to: string, pathname: string, search: string): boolean {
  const [path, query] = to.split('?')
  if (!query) return pathname === path || pathname.startsWith(path + '/')
  const want = new URLSearchParams(query)
  const tab = want.get('tab')
  if (tab && pathname === `${path}/${tab}`) return true
  if (pathname !== path) return false
  const got = new URLSearchParams(search)
  for (const [k, v] of want) if (got.get(k) !== v) return false
  return true
}

/**
 * Ô đang sáng: `to` của tab khớp DÀI NHẤT; không tab nào khớp mà một mục Thêm khớp → 'more' (nút Thêm sáng như app);
 * còn lại null.
 */
export function activePhoneTab(
  pathname: string,
  search: string,
  tabs: PhoneLink[],
  more: PhoneMoreItem[],
): string | 'more' | null {
  let best: string | null = null
  for (const t of tabs) {
    if (linkMatches(t.to, pathname, search) && (!best || t.to.length > best.length)) best = t.to
  }
  if (best) return best
  return more.some((m) => m.kind === 'link' && linkMatches(m.to, pathname, search)) ? 'more' : null
}

/** Trang đơn của cấp dưới — không còn ở menu Quản lý (31/10/2026), mở từ "Xem đơn" trên sơ đồ. */
const NETWORK_ORDER_PAGES = ['/agency/orders', '/ctv/orders']

/**
 * Đường dẫn để tìm ô / mục đang sáng: trang đơn của cấp dưới mà menu không có mục riêng thì coi như đang ở Hệ thống
 * ('/network'); còn lại giữ nguyên. Menu admin vẫn có mục /agency/orders → không đổi.
 */
export function navMatchPath(pathname: string, items: readonly object[]): string {
  const page = NETWORK_ORDER_PAGES.find((x) => pathname === x || pathname.startsWith(x + '/'))
  return page && !items.some((i) => 'to' in i && i.to === page) ? '/network' : pathname
}

/** Ô "Cài đặt" ghim cuối thanh tab điện thoại — mở Cài đặt, nơi gom cả menu tài khoản cũ. */
export const PHONE_SETTINGS: PhoneLink = { to: '/settings', label: 'Cài đặt', icon: 'settings' }

/**
 * Các ô của thanh tab điện thoại (trừ Cài đặt ghim riêng): 4 tab chính, `extra` (Gom Đơn), rồi các mục của "Thêm" cũ —
 * mục đổi chế độ luôn cuối. Thanh rộng hơn màn thì vuốt ngang.
 */
export function phoneDockItems(
  surface: PhoneSurface,
  tenantType: string | null | undefined,
  canAccess: (to: string) => boolean,
  extra: PhoneMoreItem[] = [],
): PhoneMoreItem[] {
  const tabs: PhoneMoreItem[] = phoneTabs(surface, canAccess).map((t) => ({ kind: 'link', ...t }))
  const more = phoneMoreItems(surface, tenantType, canAccess).filter((i) => i.kind !== 'link' || pathOf(i.to) !== PHONE_SETTINGS.to)
  return [...tabs, ...extra, ...more]
}

/**
 * Ô sáng của thanh tab: ô có `to` khớp DÀI NHẤT; không ô nào khớp mà trang thuộc Cài đặt (chính nó, hoặc một mục của
 * menu tài khoản cũ — `settingsLinks`) → '/settings'; còn lại null.
 */
export function activeDockTab(pathname: string, search: string, items: PhoneMoreItem[], settingsLinks: string[]): string | null {
  let best: string | null = null
  for (const i of items) {
    if (i.kind === 'link' && linkMatches(i.to, pathname, search) && (!best || i.to.length > best.length)) best = i.to
  }
  if (best) return best
  return [PHONE_SETTINGS.to, ...settingsLinks].some((to) => linkMatches(to, pathname, search)) ? PHONE_SETTINGS.to : null
}
